import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Slider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * Pure fee calculation — extracted for testability.
 */
export const calculateAnnualFees = ({ monthlyFee, atmUses, eTransfers, atmFee, eTransferFee }) => {
  const feeCost = monthlyFee * 12;
  const atmCost = atmUses * atmFee * 12;
  const eCost = eTransfers * eTransferFee * 12;
  return { feeCost, atmCost, eCost, total: feeCost + atmCost + eCost };
};

const FeeKiller = () => {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const [monthlyFee, setMonthlyFee] = useState(4.95);
  const [atmUses, setAtmUses] = useState(4);
  const [eTransfers, setETransfers] = useState(8);
  const [minBalance, setMinBalance] = useState('');

  // Editable assumptions
  const [atmFee, setAtmFee] = useState(3);
  const [eTransferFee, setETransferFee] = useState(1);

  const annualCost = useMemo(
    () => calculateAnnualFees({ monthlyFee, atmUses, eTransfers, atmFee, eTransferFee }),
    [monthlyFee, atmUses, eTransfers, atmFee, eTransferFee],
  );

  const balanceNum = parseFloat(minBalance) || 0;
  const canWaiveFee = balanceNum >= 3000;

  return (
    <Box
      role="region"
      aria-label={t('banking.feeKiller.ariaLabel', 'Calculadora de fees bancarios')}
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E6E9EE',
        borderRadius: '16px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
        p: { xs: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <SavingsOutlinedIcon sx={{ fontSize: '1.1rem', color: '#D32F2F' }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#5F6B7A',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {t('banking.feeKiller.overline', 'Calculadora')}
        </Typography>
      </Box>
      <Typography
        variant="h6"
        component="h3"
        sx={{ fontWeight: 700, color: '#1A1A1A', mb: 0.5 }}
      >
        {t('banking.feeKiller.title', '¿Te están cobrando de más?')}
      </Typography>
      <Typography sx={{ fontSize: '14px', color: '#5F6B7A', mb: 3 }}>
        {t('banking.feeKiller.subtitle', 'Calcula tu costo real y encuentra cómo ahorrar.')}
      </Typography>

      {/* Slider 1 - Monthly fee */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography component="label" htmlFor="fee-slider" sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('banking.feeKiller.monthlyFeeLabel', 'Cuota mensual del banco')}
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1E3A5F' }}>
            {t('banking.feeKiller.perMonth', '{{amount}}/mes', { amount: `$${monthlyFee.toFixed(2)}` })}
          </Typography>
        </Box>
        <Slider
          id="fee-slider"
          value={monthlyFee}
          onChange={(_, v) => setMonthlyFee(v)}
          min={0}
          max={30}
          step={0.05}
          aria-label={t('banking.feeKiller.monthlyFeeLabel', 'Cuota mensual del banco')}
          aria-valuetext={t('banking.feeKiller.monthlyFeeValue', '${{amount}} por mes', { amount: monthlyFee.toFixed(2) })}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': { width: 18, height: 18, boxShadow: '0 2px 6px rgba(0,0,0,0.15)', '&:focus-visible': { boxShadow: '0 0 0 4px rgba(30,58,95,0.25)' } },
            '& .MuiSlider-track': { height: 6 },
            '& .MuiSlider-rail': { height: 6, bgcolor: '#E6E9EE' },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>$0</Typography>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>{`$30/${t("budgetPlanner.common.month", "mes")}`}</Typography>
        </Box>
      </Box>

      {/* Slider 2 - ATM uses */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography component="label" htmlFor="atm-slider" sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('banking.feeKiller.atmLabel', 'Retiros ATM fuera de red / mes')}
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1E3A5F' }}>
            {atmUses}x
          </Typography>
        </Box>
        <Slider
          id="atm-slider"
          value={atmUses}
          onChange={(_, v) => setAtmUses(v)}
          min={0}
          max={20}
          step={1}
          aria-label={t('banking.feeKiller.atmLabel', 'Retiros ATM fuera de red / mes')}
          aria-valuetext={t('banking.feeKiller.atmValue', '{{count}} retiros por mes', { count: atmUses })}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': { width: 18, height: 18, boxShadow: '0 2px 6px rgba(0,0,0,0.15)', '&:focus-visible': { boxShadow: '0 0 0 4px rgba(30,58,95,0.25)' } },
            '& .MuiSlider-track': { height: 6 },
            '& .MuiSlider-rail': { height: 6, bgcolor: '#E6E9EE' },
          }}
        />
      </Box>

      {/* Slider 3 - E-Transfers */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography component="label" htmlFor="etransfer-slider" sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('banking.feeKiller.etransferLabel', 'E-Transfers enviados / mes')}
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1E3A5F' }}>
            {eTransfers}x
          </Typography>
        </Box>
        <Slider
          id="etransfer-slider"
          value={eTransfers}
          onChange={(_, v) => setETransfers(v)}
          min={0}
          max={30}
          step={1}
          aria-label={t('banking.feeKiller.etransferLabel', 'E-Transfers enviados / mes')}
          aria-valuetext={t('banking.feeKiller.etransferValue', '{{count}} e-transfers por mes', { count: eTransfers })}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': { width: 18, height: 18, boxShadow: '0 2px 6px rgba(0,0,0,0.15)', '&:focus-visible': { boxShadow: '0 0 0 4px rgba(30,58,95,0.25)' } },
            '& .MuiSlider-track': { height: 6 },
            '& .MuiSlider-rail': { height: 6, bgcolor: '#E6E9EE' },
          }}
        />
      </Box>

      {/* Optional - Minimum balance */}
      <Box sx={{ mb: 2.5 }}>
        <Typography component="label" htmlFor="balance-input" sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', mb: 0.5, display: 'block' }}>
          {t('banking.feeKiller.balanceLabel', 'Saldo mínimo que puedes mantener')} <Typography component="span" sx={{ fontSize: '12px', color: '#5F6B7A', fontWeight: 400 }}>{t('banking.feeKiller.optional', '(opcional)')}</Typography>
        </Typography>
        <TextField
          id="balance-input"
          value={minBalance}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setMinBalance(v);
          }}
          placeholder={t('banking.feeKiller.balancePlaceholder', 'ej. 3000')}
          size="small"
          inputProps={{ inputMode: 'decimal', 'aria-label': t('banking.feeKiller.balanceAria', 'Saldo mínimo en dólares') }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{
            maxWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '14px',
              bgcolor: '#F7F9FC',
              '& fieldset': { borderColor: '#E6E9EE' },
              '&:hover fieldset': { borderColor: '#5F6B7A' },
              '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
            },
          }}
        />
        {canWaiveFee && (
          <Typography sx={{ fontSize: '12px', color: '#16A34A', mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: '14px' }} />
            {t('banking.feeKiller.waiveFeeHint', 'Algunos bancos eliminan la cuota con este saldo.')}
          </Typography>
        )}
      </Box>

      {/* Editable assumptions */}
      <Accordion
        elevation={0}
        sx={{
          mb: 3,
          bgcolor: 'transparent',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': { px: 0, minHeight: 'auto' },
          '& .MuiAccordionSummary-content': { my: 0.5 },
          '& .MuiAccordionDetails-root': { px: 0, pt: 1 },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A', fontWeight: 500 }}>
            {t('banking.feeKiller.editAssumptions', 'Editar supuestos')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box>
              <Typography component="label" htmlFor="atm-fee-input" sx={{ fontSize: '12px', color: '#5F6B7A', mb: 0.5, display: 'block' }}>
                {t('banking.feeKiller.atmFeeLabel', 'Fee por ATM')}
              </Typography>
              <TextField
                id="atm-fee-input"
                value={atmFee}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v >= 0) setAtmFee(v);
                }}
                size="small"
                type="number"
                inputProps={{ min: 0, max: 10, step: 0.5, inputMode: 'decimal', 'aria-label': t('banking.feeKiller.atmFeeAria', 'Fee por retiro ATM') }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', bgcolor: '#F7F9FC' },
                }}
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor="etransfer-fee-input" sx={{ fontSize: '12px', color: '#5F6B7A', mb: 0.5, display: 'block' }}>
                {t('banking.feeKiller.etransferFeeLabel', 'Fee por e-Transfer')}
              </Typography>
              <TextField
                id="etransfer-fee-input"
                value={eTransferFee}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v >= 0) setETransferFee(v);
                }}
                size="small"
                type="number"
                inputProps={{ min: 0, max: 5, step: 0.25, inputMode: 'decimal', 'aria-label': t('banking.feeKiller.etransferFeeLabel', 'Fee por e-Transfer') }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', bgcolor: '#F7F9FC' },
                }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Result */}
      <Box
        role="status"
        aria-live="polite"
        sx={{
          p: 3,
          bgcolor: annualCost.total > 200 ? '#FFEBEE' : annualCost.total > 50 ? '#FFF8E1' : '#E8F5E9',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: annualCost.total > 200 ? '#FECACA' : annualCost.total > 50 ? '#FDE68A' : '#BBF7D0',
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
          {t('banking.feeKiller.annualCostTitle', 'Tu costo anual estimado')}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontFeatureSettings: "'tnum'",
            fontSize: '32px',
            color: annualCost.total > 200 ? '#DC2626' : annualCost.total > 50 ? '#D97706' : '#16A34A',
            lineHeight: 1,
            mb: 1.5,
          }}
        >
          ${annualCost.total.toFixed(0)}
          <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500, color: '#5F6B7A', ml: 0.5 }}>
            / {t('budgetPlanner.common.year', 'año')}
          </Typography>
        </Typography>

        {/* Breakdown */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>{t('banking.feeKiller.breakdownFee', 'Cuota mensual')}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>${annualCost.feeCost.toFixed(0)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>{t('banking.feeKiller.breakdownAtm', 'Fees ATM ({{uses}}x/mes x ${{fee}})', { uses: atmUses, fee: atmFee })}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>${annualCost.atmCost.toFixed(0)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>{t('banking.feeKiller.breakdownEtransfer', 'Fees e-Transfer ({{uses}}x/mes x ${{fee}})', { uses: eTransfers, fee: eTransferFee })}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>${annualCost.eCost.toFixed(0)}</Typography>
          </Box>
        </Box>

        {/* Recommendations bullet list */}
        {annualCost.total > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
              {t('banking.feeKiller.tipsTitle', 'Qué puedes hacer:')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
              {annualCost.feeCost > 0 && (
                <li>
                  <Typography sx={{ fontSize: '12px', color: '#1A1A1A', lineHeight: 1.5 }}>
                    {t('banking.feeKiller.tipNoFee', 'Busca cuenta sin fees (Simplii, Tangerine, EQ Bank)')}
                  </Typography>
                </li>
              )}
              {annualCost.atmCost > 0 && (
                <li>
                  <Typography sx={{ fontSize: '12px', color: '#1A1A1A', lineHeight: 1.5 }}>
                    {t('banking.feeKiller.tipAvoidAtm', 'Evita ATM fuera de red — usa tu banco o paga con tarjeta')}
                  </Typography>
                </li>
              )}
              {canWaiveFee && annualCost.feeCost > 0 && (
                <li>
                  <Typography sx={{ fontSize: '12px', color: '#1A1A1A', lineHeight: 1.5 }}>
                    {t('banking.feeKiller.tipMinBalance', 'Considera mantener saldo mínimo si tu banco lo permite')}
                  </Typography>
                </li>
              )}
              {annualCost.eCost > 0 && (
                <li>
                  <Typography sx={{ fontSize: '12px', color: '#1A1A1A', lineHeight: 1.5 }}>
                    {t('banking.feeKiller.tipFreeEtransfer', 'Cambia a banco con e-Transfers ilimitados gratis')}
                  </Typography>
                </li>
              )}
            </Box>
          </Box>
        )}

        {/* CTAs */}
        {annualCost.total > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component={RouterLink}
              to={lp('accountComparator')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 2.5,
                minHeight: 44,
                bgcolor: '#1E3A5F',
                '&:hover': { bgcolor: '#1A3152' },
                '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
              }}
            >
              {t('banking.feeKiller.ctaCompare', 'Comparar cuentas sin fees')}
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to={lp('banking')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 2.5,
                minHeight: 44,
                borderColor: '#1E3A5F',
                color: '#1E3A5F',
                '&:hover': { bgcolor: 'rgba(30,58,95,0.04)', borderColor: '#1A3152' },
                '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
              }}
            >
              {t('banking.feeKiller.ctaHighInterest', 'Ver cuentas con alto interés')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FeeKiller;
